import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAbility } from '@casl/react';
import {
    AlertTriangle, ArrowLeft, Ban, Camera, Check, CheckCircle2, ClipboardList, Download, Expand,
    FileText, Images, Loader2, Lock, MessageSquare, Paperclip, Pencil, Plus, Send, Sparkles, Trash2, Undo2, X,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Combobox } from '../../components/ui/combobox';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import AccessRestricted from '../../components/AccessRestricted';
import { ImageLightbox } from '../../components/ui/image-lightbox';
import { AbilityContext } from '../../context/casl/AbilityContext';
import { ACTIONS, RESOURCES } from '../../constants/permissions.constants';
import {
    AiStatus, LineCondition, Product, ReturnOrderDetail, ReturnOrderMeta, ReturnStatus, StockDocument,
} from '../../types/stock.types';
import { productsApi, returnsApi, stockAiApi } from '../../utils/stock-api';
import { ApiError } from '../../utils/api';
import GenerateOrderDialog, { GeneratedLine } from './components/GenerateOrderDialog';
import { cn } from '../../components/ui/utils';
import ReturnStatusPill, { statusLabel } from './components/ReturnStatusPill';
import SignaturePad from './components/SignaturePad';

const CONDITION_STYLES: Record<LineCondition, string> = {
    good: 'text-emerald-700 bg-emerald-50',
    damaged: 'text-amber-700 bg-amber-50',
    lost: 'text-red-700 bg-red-50',
};

const CONDITION_HELP: Record<LineCondition, string> = {
    good: 'Returns to available stock',
    damaged: 'Goes to quarantine, not available stock',
    lost: 'Never enters stock — feeds the shortfall report',
};

/** A photo queued in the batch-upload dialog, waiting for its slot to be chosen. */
interface BatchPhoto {
    id: string;
    file: File;
    previewUrl: string;
    slotKey: string;
}

/**
 * Return Order detail — STOCK.md §5.3, §5.4.
 *
 * Two gated steps. Photos first, and material entry stays locked until all six
 * prescribed slots are filled (§5.3) — the gate exists so the truck is photographed
 * before anyone starts counting, which is the whole evidentiary point.
 *
 * THE COUNT IS BLIND (§5.4). Nothing on this page shows what the project was sent, and
 * no endpoint would supply it. Variance is a report produced after completion (§5.4b).
 */
export default function ReturnOrderDetailPage() {
    const ability = useAbility(AbilityContext);
    const canRead = ability.can(ACTIONS.READ, RESOURCES.STOCK_RETURN_ORDER);
    const canAttachDocs = ability.can(ACTIONS.CREATE, RESOURCES.STOCK_PHOTO);
    const canDeleteDocs = ability.can(ACTIONS.DELETE, RESOURCES.STOCK_DOCUMENT);
    /**
     * Reopening a signed-off count (completed -> assigned) is its own grant, held by the
     * Transport Manager / Yard Manager — the count crew's `update` must not reopen a
     * count that has already been signed off. The server filters availableTransitions the
     * same way; the button below also checks the status so it never appears on `open`.
     */
    const canReturnToCount = ability.can(ACTIONS.RETURN_TO_COUNT, RESOURCES.STOCK_RETURN_ORDER);
    const { id, yardId } = useParams<{ id: string; yardId?: string }>();
    const navigate = useNavigate();
    /** Back should return to the list the user actually came from (STOCK-MENU.md §2). */
    const listHref = yardId ? `/stock/yards/${yardId}/returns` : '/stock/returns';

    const [order, setOrder] = useState<ReturnOrderDetail | null>(null);
    /** Server-side refusal (yard scoping) — rendered as a page, not eternal skeletons (UAT B9). */
    const [accessDenied, setAccessDenied] = useState<string | null>(null);
    const [meta, setMeta] = useState<ReturnOrderMeta | null>(null);
    const [products, setProducts] = useState<Product[]>([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState<string | null>(null);

    // Line entry
    const [productId, setProductId] = useState<string>('');
    const [quantity, setQuantity] = useState<string>('');
    const [condition, setCondition] = useState<LineCondition>('good'); // §5.4: Good is default
    const [aiOpen, setAiOpen] = useState(false);
    const [aiStatus, setAiStatus] = useState<AiStatus | null>(null);
    // Inline quantity edit on a saved line — ✓/Enter commits, ✕/Escape cancels.
    const [editingLineId, setEditingLineId] = useState<number | null>(null);
    const [editQuantity, setEditQuantity] = useState<string>('');

    // Signature
    const [signatureData, setSignatureData] = useState<string | null>(null);
    const [typedName, setTypedName] = useState('');

    // Dialogs
    const [voidOpen, setVoidOpen] = useState(false);
    const [voidReason, setVoidReason] = useState('');
    const [voidCode, setVoidCode] = useState('other');
    const [enterOpen, setEnterOpen] = useState(false);
    const [externalRef, setExternalRef] = useState('');
    const [comment, setComment] = useState('');
    /** Which captured truck photo the viewer is open on, or null when it is closed. */
    const [photoIndex, setPhotoIndex] = useState<number | null>(null);

    // Batch photo upload (Step 1) — several files picked at once, each assigned a slot.
    const [batchPhotos, setBatchPhotos] = useState<BatchPhoto[]>([]);
    const [batchOpen, setBatchOpen] = useState(false);

    const photoInputs = useRef<Record<string, HTMLInputElement | null>>({});
    const batchInput = useRef<HTMLInputElement | null>(null);
    const docInput = useRef<HTMLInputElement | null>(null);
    /** The source document pending removal (§12.4) — confirm before it goes. */
    const [docToDelete, setDocToDelete] = useState<StockDocument | null>(null);

    const load = useCallback(async () => {
        if (!id) return;
        try {
            setLoading(true);
            setOrder(await returnsApi.get(Number(id)));
            setAccessDenied(null);
        } catch (err) {
            // A 403 here is yard scoping, not a transient failure — show it on the page
            // (the server's own wording) instead of leaving skeletons up forever (UAT B9).
            if (err instanceof ApiError && err.status === 403) {
                setAccessDenied(err.data?.message || err.message);
                return;
            }
            toast.error(err instanceof Error ? err.message : 'Failed to load return order');
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => { if (canRead) load(); }, [canRead, load]);

    useEffect(() => {
        if (!canRead) return;
        returnsApi.meta().then(setMeta).catch(() => undefined);
        productsApi.list({ status: 'active' }).then(setProducts).catch(() => undefined);
        // §2.6 — decides between the CTA and the manual-entry microcopy.
        stockAiApi.status().then(setAiStatus).catch(() =>
            setAiStatus({ available: false, model: '', message: null }));
    }, [canRead]);

    /**
     * Writes reviewed AI lines onto the return, one call each.
     *
     * Every line lands with the CURRENT condition selector value — §5.4 defaults that to
     * Good. The parser reads quantities, not condition; a yardsman who has just counted
     * damaged gear should set the selector first, and the caption says so.
     */
    const handleGenerated = async (
        generated: GeneratedLine[],
        _rawInputText: string,
        _files: File[],
        usageId: number | null,
    ) => {
        if (!order) return;
        await run('ai', async () => {
            for (const line of generated) {
                await returnsApi.addLine(order.id, {
                    productId: line.productId,
                    quantity: line.quantity,
                    condition,
                    source: 'ai_matched',
                    originalText: line.originalText,
                    aiConfidence: line.confidence ?? undefined,
                    // The first line links the parse to this return; the id is
                    // idempotent, so the rest are harmless.
                    aiUsageId: usageId,
                });
            }
        }, `${generated.length} line(s) added as ${condition}`);
    };

    const photosByStage = useMemo(() => {
        const map = new Map<string, ReturnOrderDetail['photos'][number]>();
        (order?.photos ?? []).forEach((p) => { if (!map.has(p.stage)) map.set(p.stage, p); });
        return map;
    }, [order]);

    const slots = meta?.photoSlots ?? [];

    /**
     * Only the filled slots are viewable, so the lightbox index counts those rather than
     * all six — otherwise arrowing through would land on empty capture tiles.
     */
    const capturedSlots = useMemo(
        () => slots.filter((s) => photosByStage.has(s.key)),
        [slots, photosByStage],
    );

    /**
     * Slots still waiting for a photo. The batch dialog can only fill these — replacing
     * an occupied slot is the single-photo edit, never part of a batch.
     */
    const emptySlots = useMemo(
        () => slots.filter((s) => !photosByStage.has(s.key)),
        [slots, photosByStage],
    );

    const photoCount = order?.photos.length ?? 0;
    const minPhotos = meta?.minPhotos ?? 6;
    const photoGatePassed = slots.length > 0 && slots.every((s) => photosByStage.has(s.key));

    /** Counting is only permitted while Assigned — matches the server's guard. */
    const countable = order?.status === 'assigned';
    const signature = order?.signatures.find((s) => s.role === 'return') ?? null;

    const run = async (key: string, fn: () => Promise<any>, successMessage?: string) => {
        try {
            setBusy(key);
            await fn();
            if (successMessage) toast.success(successMessage);
            await load();
        } catch (err: any) {
            toast.error(err?.data?.message || err?.message || 'Something went wrong');
        } finally {
            setBusy(null);
        }
    };

    /**
     * Best-effort geolocation: §12.1 wants it "where the device permits", so a
     * refusal must not block the capture.
     */
    const getGeo = () => new Promise<{ lat?: number; lng?: number }>((resolve) => {
        if (!navigator.geolocation) return resolve({});
        const done = (v: { lat?: number; lng?: number }) => resolve(v);
        navigator.geolocation.getCurrentPosition(
            (pos) => done({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
            () => done({}),
            { timeout: 5000 }
        );
    });

    const handlePhoto = async (stageKey: string, file: File | null) => {
        if (!file || !order) return;
        const geo = await getGeo();

        await run(`photo:${stageKey}`,
            () => returnsApi.uploadPhoto(order.id, stageKey, file, geo),
            'Photo captured');
    };

    /** §12.4 — source paperwork can be attached at any status; there is no gate. */
    const handleDocuments = async (files: FileList | null) => {
        if (!files?.length || !order) return;
        await run('documents',
            () => returnsApi.uploadDocuments(order.id, Array.from(files), 'upload'),
            'Document(s) attached');
    };

    /**
     * All six truck photos as one ZIP, named for their slots — what the Transport
     * Manager keeps. Deliberately not routed through `run()`: the download changes
     * nothing about the order, so there is nothing to reload afterwards.
     */
    const downloadPhotos = async () => {
        if (!order) return;
        try {
            setBusy('downloadPhotos');
            await returnsApi.downloadPhotos(order.id, order.orderNumber);
        } catch (err) {
            toast.error(err instanceof Error ? err.message : 'Could not download the photos');
        } finally {
            setBusy(null);
        }
    };

    const handleBatchFiles = (files: FileList | null) => {
        if (!files?.length) return;
        const picked = Array.from(files);
        if (picked.length > emptySlots.length) {
            toast.error(`Only ${emptySlots.length} empty slot(s) left — the extra photo(s) were ignored.`);
        }
        setBatchPhotos(picked.slice(0, emptySlots.length).map((file, i) => ({
            id: `${Date.now()}:${i}`,
            file,
            previewUrl: URL.createObjectURL(file),
            slotKey: '',
        })));
        setBatchOpen(true);
    };

    const closeBatch = () => {
        batchPhotos.forEach((b) => URL.revokeObjectURL(b.previewUrl));
        setBatchPhotos([]);
        setBatchOpen(false);
    };

    const setBatchSlot = (rowId: string, slotKey: string) =>
        setBatchPhotos((rows) => rows.map((r) => (r.id === rowId ? { ...r, slotKey } : r)));

    const removeBatchRow = (rowId: string) =>
        setBatchPhotos((rows) => {
            const removed = rows.find((r) => r.id === rowId);
            if (removed) URL.revokeObjectURL(removed.previewUrl);
            return rows.filter((r) => r.id !== rowId);
        });

    const handleBatchSave = async () => {
        if (!order || batchPhotos.some((b) => !b.slotKey)) return;
        await run('batchPhotos', async () => {
            // One geolocation fix for the whole batch — they were all picked together.
            const geo = await getGeo();
            for (const b of batchPhotos) {
                await returnsApi.uploadPhoto(order.id, b.slotKey, b.file, geo);
            }
        }, `${batchPhotos.length} photo(s) uploaded`);
        closeBatch();
    };

    const handleAddLine = async () => {
        if (!order) return;
        if (!productId) return toast.error('Pick a product first');
        const qty = Number(quantity);
        if (!Number.isFinite(qty) || qty < 0) return toast.error('Enter a quantity');

        await run('addLine', async () => {
            await returnsApi.addLine(order.id, {
                productId: Number(productId), quantity: qty, condition,
            });
            setProductId(''); setQuantity(''); setCondition('good');
        });
    };
    const startEditLine = (lineId: number, currentQty: number) => {
        setEditQuantity(String(currentQty));
        setEditingLineId(lineId);
    };

    const commitEditLine = async (lineId: number) => {
        if (!order) return;
        const qty = Number(editQuantity);
        if (editQuantity.trim() === '' || !Number.isFinite(qty) || qty < 0) {
            toast.error('Quantity must be zero or more');
            return;
        }
        setEditingLineId(null);
        await run(`updateLine:${lineId}`,
            () => returnsApi.updateLine(order.id, lineId, { quantity: qty }),
            'Quantity updated');
    };

    const handleSign = async () => {
        if (!order || !signatureData || !typedName.trim()) {
            return toast.error('Draw a signature and type your name');
        }
        await run('sign',
            () => returnsApi.sign(order.id, { signatureUrl: signatureData, typedName: typedName.trim() }),
            'Signed');
    };

    const handleTransition = async (to: ReturnStatus, extra: Record<string, any> = {}) => {
        if (!order) return;
        await run(`transition:${to}`,
            () => returnsApi.transition(order.id, { to, ...extra }),
            `Moved to ${statusLabel(to)}`);
    };

    if (!canRead) return <AccessRestricted />;

    if (accessDenied) {
        return <AccessRestricted accessMessage={accessDenied}
                                 backMessage="Back to Return Orders" navigateTo={listHref} />;
    }

    if (loading || !order) {
        return (
            <div className="space-y-3 animate-pulse p-2 md:p-0">
                {[0, 1, 2].map((i) => <div key={i} className="h-24 bg-gray-300 rounded-md" />)}
            </div>
        );
    }

    const available = order.availableTransitions ?? [];
    const isTerminal = order.status === 'entered' || order.status === 'voided';

    return (
        <div className="space-y-4 md:space-y-6 p-2 md:p-0 max-w-5xl">
            {/* Header */}
            <div>
                <Button variant="ghost" size="sm" onClick={() => navigate(listHref)} className="mb-2 -ml-2">
                    <ArrowLeft size={16} className="mr-1.5" />Return Orders
                </Button>
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                    <div>
                        <div className="flex items-center gap-3 flex-wrap">
                            <h1 className="text-2xl font-bold font-mono">{order.orderNumber}</h1>
                            <ReturnStatusPill status={order.status} />
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">
                            {order.project?.projectName}
                            {order.project?.projectNumber ? ` (${order.project.projectNumber})` : ''}
                            {order.yard ? ` · ${order.yard.yardName}` : ''}
                        </p>
                    </div>

                    <div className="flex flex-wrap gap-2">
                        {available.includes('assigned') && !order.assignedTo && (
                            <Button
                                variant="outline"
                                onClick={() => run('assign', () => returnsApi.assign(order.id), 'Assigned to you')}
                                disabled={busy !== null}
                            >
                                Start counting
                            </Button>
                        )}
                        {/* The one way back into the count once it has been signed off:
                            add lines that were missed, or a line that turned up after
                            sign-off. Transport Manager / Yard Manager only — the
                            permission is `return_to_count:stock_return_order`, which the
                            count crew's `update`/`assign` do not carry. Photos and the
                            existing signature stay on the order; the count reopens and a
                            fresh signature is required before it can be completed again. */}
                        {order.status === 'completed' && canReturnToCount && (
                            <Button
                                variant="outline"
                                onClick={() => handleTransition('assigned')}
                                disabled={busy !== null}
                            >
                                {busy === 'transition:assigned'
                                    ? <Loader2 size={16} className="animate-spin mr-2" />
                                    : <Undo2 size={16} className="mr-2" />}
                                Return to count
                            </Button>
                        )}
                        {available.includes('completed') && (
                            <Button
                                onClick={() => handleTransition('completed')}
                                disabled={busy !== null}
                            >
                                {busy === 'transition:completed'
                                    ? <Loader2 size={16} className="animate-spin mr-2" />
                                    : <CheckCircle2 size={16} className="mr-2" />}
                                Mark Complete
                            </Button>
                        )}
                        {available.includes('ready_to_enter') && (
                            <Button onClick={() => handleTransition('ready_to_enter')} disabled={busy !== null}>
                                <Check size={16} className="mr-2" />Ready to Enter
                            </Button>
                        )}
                        {available.includes('entered') && (
                            <Button onClick={() => { setExternalRef(''); setEnterOpen(true); }} disabled={busy !== null}>
                                <Send size={16} className="mr-2" />Mark Entered
                            </Button>
                        )}
                        {available.includes('voided') && (
                            <Button
                                variant="outline"
                                className="text-destructive"
                                onClick={() => { setVoidReason(''); setVoidOpen(true); }}
                                disabled={busy !== null}
                            >
                                <Ban size={16} className="mr-2" />Void
                            </Button>
                        )}
                    </div>
                </div>
            </div>

            {/* Terminal / void context */}
            {order.status === 'voided' && order.voidReason && (
                <Card className="border-red-200 bg-red-50/50">
                    <CardContent className="p-4 text-sm">
                        <p className="font-medium text-red-800">Voided — {order.voidReasonCode?.replace(/_/g, ' ')}</p>
                        <p className="text-red-700 mt-1">{order.voidReason}</p>
                        <p className="text-xs text-red-600 mt-2">
                            The full record is preserved in the audit trail below.
                        </p>
                    </CardContent>
                </Card>
            )}
            {order.status === 'entered' && (
                <Card className="border-emerald-200 bg-emerald-50/50">
                    <CardContent className="p-4 text-sm">
                        <p className="font-medium text-emerald-800">
                            Entered into the external stock system
                        </p>
                        <p className="text-emerald-700 mt-1">
                            Reference <span className="font-mono font-semibold">{order.externalRef}</span>
                            {order.enteredBy ? ` · ${order.enteredBy.name}` : ''}
                        </p>
                        <p className="text-xs text-emerald-600 mt-2">
                            This is terminal and cannot be edited. If the reference is wrong, add a comment explaining it.
                        </p>
                    </CardContent>
                </Card>
            )}

            {/* Summary */}
            <Card>
                <CardContent className="p-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                        <p className="text-muted-foreground text-xs">Job Number</p>
                        <p className="font-mono font-medium">{order.project?.projectNumber ?? '–'}</p>
                    </div>
                    <div>
                        <p className="text-muted-foreground text-xs">Return Date</p>
                        <p className="font-medium">{order.returnDate ?? '–'}</p>
                    </div>
                    <div>
                        <p className="text-muted-foreground text-xs">Restocking Date</p>
                        <p className="font-medium">{order.restockingDate ?? '–'}</p>
                    </div>
                    <div>
                        <p className="text-muted-foreground text-xs">Assigned To</p>
                        <p className="font-medium">{order.assignedTo?.name ?? 'Unassigned'}</p>
                    </div>
                    <div>
                        <p className="text-muted-foreground text-xs">Created By</p>
                        <p className="font-medium">{order.createdBy?.name ?? '–'}</p>
                    </div>
                </CardContent>
            </Card>

            {/* Step 1 — photos */}
            <Card>
                <CardContent className="p-4 md:p-6">
                    <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
                        <h2 className="font-semibold flex items-center gap-2">
                            <Camera size={18} />Step 1 — Truck photos
                        </h2>
                        <div className="flex items-center gap-3">
                            {/* The Transport Manager's copy of the evidence — the whole
                                set in one ZIP, entries named for their slots. Nothing to
                                download until a photo exists. */}
                            {photoCount > 0 && (
                                <Button
                                    variant="outline"
                                    size="sm"
                                    disabled={busy !== null}
                                    onClick={() => void downloadPhotos()}
                                >
                                    {busy === 'downloadPhotos'
                                        ? <Loader2 size={14} className="animate-spin mr-1.5" />
                                        : <Download size={14} className="mr-1.5" />}
                                    Download all
                                </Button>
                            )}
                            {!isTerminal && emptySlots.length > 0 && (
                                <Button
                                    variant="outline"
                                    size="sm"
                                    disabled={busy !== null}
                                    onClick={() => batchInput.current?.click()}
                                >
                                    {busy === 'batchPhotos'
                                        ? <Loader2 size={14} className="animate-spin mr-1.5" />
                                        : <Images size={14} className="mr-1.5" />}
                                    Upload multiple
                                </Button>
                            )}
                            <span className={cn('text-sm font-medium',
                                photoGatePassed ? 'text-emerald-700' : 'text-muted-foreground')}>
                                {photoCount}/{minPhotos}
                            </span>
                        </div>
                    </div>
                    {/*
                      No capture attribute here: a batch means picking several frames at
                      once, which the camera UI cannot do — the chooser still offers the
                      camera on phones. Same client-side limitation as the single inputs.
                    */}
                    <input
                        ref={batchInput}
                        type="file"
                        accept="image/*"
                        multiple
                        className="hidden"
                        onChange={(e) => {
                            handleBatchFiles(e.target.files);
                            e.target.value = '';
                        }}
                    />
                    <p className="text-sm text-muted-foreground mb-4">
                        All six angles are required before counting can start. Capture each slot
                        singly, or upload several at once and assign them to slots. Photos are
                        timestamped and hashed by the server.
                    </p>

                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        {slots.map((slot) => {
                            const photo = photosByStage.get(slot.key);
                            const uploading = busy === `photo:${slot.key}`;
                            return (
                                <div key={slot.key} className="space-y-1.5">
                                    <div className={cn(
                                        'relative aspect-[4/3] rounded-lg border overflow-hidden bg-muted/40',
                                        photo ? 'border-emerald-300' : 'border-dashed border-border'
                                    )}>
                                        {photo ? (
                                            <>
                                                <button
                                                    type="button"
                                                    aria-label={`Open ${slot.label} photo`}
                                                    onClick={() => setPhotoIndex(
                                                        capturedSlots.findIndex((s) => s.key === slot.key))}
                                                    className="group w-full h-full focus:outline-none focus:ring-2 focus:ring-ring"
                                                >
                                                    <img src={photo.url} alt={slot.label}
                                                         className="w-full h-full object-cover transition-transform group-hover:scale-105" />
                                                    <span className="absolute inset-0 bg-black/0 group-hover:bg-black/25 transition-colors flex items-center justify-center">
                                                        <Expand size={18}
                                                                className="text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                                                    </span>
                                                </button>
                                                <span className="absolute top-1 right-1 bg-emerald-600 text-white rounded-full p-0.5 pointer-events-none">
                                                    <Check size={12} />
                                                </span>
                                                {uploading && (
                                                    <span className="absolute inset-0 bg-black/40 flex items-center justify-center pointer-events-none">
                                                        <Loader2 size={20} className="animate-spin text-white" />
                                                    </span>
                                                )}
                                                {/*
                                                  Single-photo edit: re-capturing a filled slot replaces
                                                  it (the server swaps the old row out). Kept out of the
                                                  lightbox button — nested buttons are invalid HTML.
                                                */}
                                                {!isTerminal && !uploading && (
                                                    <button
                                                        type="button"
                                                        aria-label={`Replace ${slot.label} photo`}
                                                        title="Replace photo"
                                                        onClick={() => photoInputs.current[slot.key]?.click()}
                                                        className="absolute bottom-2 right-2 z-10 bg-primary text-primary-foreground hover:bg-primary/90 rounded-full p-2 shadow-md"
                                                    >
                                                        <Pencil size={16} />
                                                    </button>
                                                )}
                                            </>
                                        ) : (
                                            <button
                                                type="button"
                                                disabled={isTerminal || uploading || busy === 'batchPhotos'}
                                                onClick={() => photoInputs.current[slot.key]?.click()}
                                                className="w-full h-full flex flex-col items-center justify-center gap-1 text-muted-foreground hover:bg-accent/40 disabled:opacity-50"
                                            >
                                                {uploading
                                                    ? <Loader2 size={20} className="animate-spin" />
                                                    : <Camera size={20} />}
                                                <span className="text-xs">Capture</span>
                                            </button>
                                        )}
                                        {/*
                                          capture="environment" asks the phone for the rear camera
                                          rather than the gallery — §12.1's camera-only rule. It is a
                                          client-side control by nature: the server cannot tell a
                                          camera frame from a picked file.
                                        */}
                                        <input
                                            ref={(el) => { photoInputs.current[slot.key] = el; }}
                                            type="file"
                                            accept="image/*"
                                            capture="environment"
                                            className="hidden"
                                            onChange={(e) => {
                                                handlePhoto(slot.key, e.target.files?.[0] ?? null);
                                                e.target.value = '';
                                            }}
                                        />
                                    </div>
                                    <p className="text-xs text-center text-muted-foreground">{slot.label}</p>
                                </div>
                            );
                        })}
                    </div>
                </CardContent>
            </Card>

            {/* Step 2 — material entry */}
            <Card className={cn(!photoGatePassed && 'opacity-60')}>
                <CardContent className="p-4 md:p-6">
                    <h2 className="font-semibold flex items-center gap-2 mb-1">
                        {photoGatePassed ? <ClipboardList size={18} /> : <Lock size={18} />}
                        Step 2 — Count the load
                    </h2>

                    {!photoGatePassed ? (
                        <p className="text-sm text-muted-foreground">
                            Locked until all {minPhotos} truck photos are captured — {photoCount} so far.
                        </p>
                    ) : (
                        <p className="text-sm text-muted-foreground mb-4">
                            Count what is physically on the truck. Nothing is pre-filled and no expected
                            figures are shown — that is deliberate, and differences are reported after
                            you complete.
                        </p>
                    )}

                    {photoGatePassed && (
                        <>
                            {/* Add a line */}
                            {countable && (
                                <div className="grid grid-cols-1 md:grid-cols-[2fr_1fr_1fr_auto] gap-2 mb-4">
                                    <Combobox
                                        options={products.map((p) => ({
                                            label: p.name,
                                            value: String(p.id),
                                            subtitle: p.sku,
                                        }))}
                                        value={productId}
                                        onChange={(v) => setProductId(v ?? '')}
                                        placeholder="Select a product"
                                        searchPlaceholder="Search by name or SKU..."
                                        emptyMessage="No matching products"
                                    />
                                    <Input
                                        type="number"
                                        inputMode="numeric"
                                        min="0"
                                        placeholder="Qty"
                                        value={quantity}
                                        onChange={(e) => setQuantity(e.target.value)}
                                    />
                                    <Select value={condition} onValueChange={(v) => setCondition(v as LineCondition)}>
                                        <SelectTrigger><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            {(meta?.conditions ?? ['good', 'damaged', 'lost']).map((c) => (
                                                <SelectItem key={c} value={c}>
                                                    <span className="capitalize">{c}</span>
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <Button onClick={handleAddLine} disabled={busy !== null}>
                                        {busy === 'addLine'
                                            ? <Loader2 size={16} className="animate-spin" />
                                            : <Plus size={16} />}
                                    </Button>
                                </div>
                            )}

                            {countable && (
                                <>
                                    <p className="text-xs text-muted-foreground mb-3">
                                        {CONDITION_HELP[condition]}. The same product can be added twice with
                                        different conditions — e.g. 40 Good and 3 Damaged.
                                    </p>

                                    {/* §2.5 — same parse-and-review flow as Deliveries. */}
                                    <div className="mb-4">
                                        {aiStatus?.available ? (
                                            <>
                                                <Button variant="outline" size="sm"
                                                        disabled={busy !== null}
                                                        onClick={() => setAiOpen(true)}>
                                                    <Sparkles size={14} className="mr-1.5" />Generate Order
                                                </Button>
                                                <p className="text-xs text-muted-foreground mt-1.5">
                                                    Type or paste what came back and have it read into lines.
                                                    They'll be added as <strong>{condition}</strong> — set the
                                                    condition above first if that's not right.
                                                </p>
                                            </>
                                        ) : (
                                            <p className="text-xs text-muted-foreground">
                                                AI parsing is unavailable — add lines manually above.
                                            </p>
                                        )}
                                    </div>
                                </>
                            )}

                            {/* Lines */}
                            {order.lines.length === 0 ? (
                                <p className="text-sm text-muted-foreground py-4 text-center">
                                    Nothing counted yet.
                                </p>
                            ) : (
                                <div className="divide-y divide-border border border-border rounded-lg">
                                    {order.lines.map((line) => (
                                        /* One row per line, but two on a phone: the product name
                                           wraps at full width (a cut-off name is a guessing game
                                           while counting), and condition/quantity/actions sit on
                                           their own line beneath it rather than fighting for width. */
                                        <div key={line.id}
                                             className="flex flex-col gap-2 p-3 sm:flex-row sm:items-center sm:gap-3">
                                            <div className="min-w-0 flex-1">
                                                <p className="font-medium sm:truncate">{line.product?.name}</p>
                                                <p className="text-xs text-muted-foreground font-mono">
                                                    {line.product?.sku}
                                                </p>
                                            </div>
                                            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 sm:shrink-0">
                                                <span className={cn(
                                                    'text-xs font-medium px-2 py-1 rounded-md capitalize',
                                                    CONDITION_STYLES[(line.condition ?? 'good') as LineCondition]
                                                )}>
                                                    {line.condition ?? 'good'}
                                                </span>
                                                {countable && editingLineId === line.id ? (
                                                    <>
                                                        <Input
                                                            type="number"
                                                            inputMode="numeric"
                                                            min="0"
                                                            autoFocus
                                                            aria-label={`Quantity for ${line.product?.name ?? 'this line'}`}
                                                            className="w-20 h-8 py-0 px-2 text-right tabular-nums"
                                                            value={editQuantity}
                                                            onChange={(e) => setEditQuantity(e.target.value)}
                                                            onKeyDown={(e) => {
                                                                if (e.key === 'Enter') {
                                                                    e.preventDefault();
                                                                    commitEditLine(line.id);
                                                                } else if (e.key === 'Escape') {
                                                                    e.preventDefault();
                                                                    setEditingLineId(null);
                                                                }
                                                            }}
                                                        />
                                                        <span className="text-xs text-muted-foreground w-12">
                                                            {line.product?.uom}
                                                        </span>
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            aria-label="Save quantity"
                                                            className="h-8 w-8"
                                                            disabled={busy !== null || editQuantity === ''}
                                                            onClick={() => commitEditLine(line.id)}
                                                        >
                                                            <Check size={16} />
                                                        </Button>
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            aria-label="Cancel edit"
                                                            className="h-8 w-8"
                                                            onClick={() => setEditingLineId(null)}
                                                        >
                                                            <X size={16} />
                                                        </Button>
                                                    </>
                                                ) : (
                                                    <>
                                                        <span className="font-medium tabular-nums sm:w-16 sm:text-right">
                                                            {line.quantity}
                                                        </span>
                                                        <span className="text-xs text-muted-foreground w-12">
                                                            {line.product?.uom}
                                                        </span>
                                                        {countable && (
                                                            <>
                                                                <Button
                                                                    variant="ghost"
                                                                    size="icon"
                                                                    aria-label={`Edit quantity for ${line.product?.name ?? 'this line'}`}
                                                                    onClick={() => startEditLine(line.id, line.quantity)}
                                                                >
                                                                    <Pencil size={16} />
                                                                </Button>
                                                                <Button
                                                                    variant="ghost"
                                                                    size="icon"
                                                                    aria-label="Remove line"
                                                                    className="text-destructive"
                                                                    disabled={busy !== null}
                                                                    onClick={() => run(`removeLine:${line.id}`,
                                                                        () => returnsApi.removeLine(order.id, line.id))}
                                                                >
                                                                    <Trash2 size={16} />
                                                                </Button>
                                                            </>
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Signature */}
                            <div className="mt-6 border-t border-border pt-4">
                                <h3 className="font-medium mb-1">Sign off</h3>
                                {signature ? (
                                    <div className="flex items-center gap-3 text-sm">
                                        <img src={signature.signatureUrl} alt="Signature"
                                             className="h-16 border border-border rounded bg-white" />
                                        <div>
                                            <p className="font-medium">{signature.typedName}</p>
                                            <p className="text-xs text-muted-foreground">
                                                Signed by {signature.user?.name} ·{' '}
                                                {new Date(signature.signedAt).toLocaleString()}
                                            </p>
                                        </div>
                                    </div>
                                ) : countable ? (
                                    <>
                                        <p className="text-sm text-muted-foreground mb-3">
                                            Your typed name is shown on the record; the signature is bound to
                                            your logged-in account.
                                        </p>
                                        <SignaturePad onChange={setSignatureData} />
                                        <div className="flex flex-col sm:flex-row gap-2 mt-2">
                                            <Input
                                                placeholder="Type your full name"
                                                value={typedName}
                                                onChange={(e) => setTypedName(e.target.value)}
                                            />
                                            <Button
                                                onClick={handleSign}
                                                disabled={busy !== null || !signatureData || !typedName.trim()}
                                            >
                                                {busy === 'sign' && <Loader2 size={16} className="animate-spin mr-2" />}
                                                Sign
                                            </Button>
                                        </div>
                                    </>
                                ) : (
                                    <p className="text-sm text-muted-foreground">Not signed.</p>
                                )}
                            </div>
                        </>
                    )}
                </CardContent>
            </Card>

            {/* §12.4 — source paperwork: PDFs/photos stored as the source of record,
                incl. the sheet the AI read. No status gate; links are re-signed per read. */}
            <Card>
                <CardContent className="p-4 md:p-6">
                    <div className="flex items-center justify-between gap-2 mb-1">
                        <h2 className="font-semibold flex items-center gap-2">
                            <Paperclip size={18} />Source documents
                        </h2>
                        {canAttachDocs && (
                            <>
                                <input
                                    ref={docInput}
                                    type="file"
                                    accept="application/pdf,image/*"
                                    multiple
                                    className="hidden"
                                    onChange={(e) => {
                                        handleDocuments(e.target.files);
                                        e.target.value = '';
                                    }}
                                />
                                <Button variant="outline" size="sm"
                                        disabled={busy !== null}
                                        onClick={() => docInput.current?.click()}>
                                    {busy === 'documents'
                                        ? <Loader2 size={14} className="animate-spin mr-1.5" />
                                        : <Plus size={14} className="mr-1.5" />}
                                    Add documents
                                </Button>
                            </>
                        )}
                    </div>
                    <p className="text-sm text-muted-foreground mb-3">
                        The paperwork behind this return, kept so the original is never lost.
                    </p>
                    {order.documents.length === 0 ? (
                        <p className="text-sm text-muted-foreground">No source documents yet.</p>
                    ) : (
                        <div className="divide-y divide-border border border-border rounded-lg">
                            {order.documents.map((d) => (
                                <div key={d.id} className="flex items-center pr-1">
                                    <a href={d.url} target="_blank" rel="noreferrer"
                                       className="flex items-center gap-3 p-3 hover:bg-accent/40 transition-colors flex-1 min-w-0">
                                        <span className="w-10 h-10 rounded-md border border-border bg-muted/40 flex items-center justify-center shrink-0">
                                            <FileText size={16} className="text-muted-foreground" />
                                        </span>
                                        <span className="min-w-0 flex-1">
                                            <span className="block text-sm font-medium truncate">{d.filename}</span>
                                            <span className="block text-xs text-muted-foreground">
                                                {d.uploadedBy?.name} · {new Date(d.createdAt).toLocaleString()}
                                            </span>
                                        </span>
                                        {d.source === 'ai_parse' && (
                                            <span className="text-[10px] font-medium text-blue-700 bg-blue-50 rounded px-1.5 py-0.5 shrink-0">
                                                AI sheet
                                            </span>
                                        )}
                                    </a>
                                    {canDeleteDocs && (
                                        <Button variant="ghost" size="icon"
                                                aria-label={`Remove ${d.filename}`}
                                                className="text-destructive shrink-0"
                                                disabled={busy !== null}
                                                onClick={() => setDocToDelete(d)}>
                                            <Trash2 size={16} />
                                        </Button>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Comments — allowed even on terminal records (§2.4) */}
            <Card>
                <CardContent className="p-4 md:p-6">
                    <h2 className="font-semibold flex items-center gap-2 mb-3">
                        <MessageSquare size={18} />Comments
                    </h2>
                    <div className="flex flex-col sm:flex-row gap-2 mb-4">
                        <Textarea
                            rows={2}
                            placeholder="Add context — always allowed, even once this is terminal."
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                        />
                        <Button
                            disabled={busy !== null || !comment.trim()}
                            onClick={() => run('comment', async () => {
                                await returnsApi.comment(order.id, comment.trim());
                                setComment('');
                            })}
                        >
                            Post
                        </Button>
                    </div>
                    {order.comments.length === 0 ? (
                        <p className="text-sm text-muted-foreground">No comments.</p>
                    ) : (
                        <div className="space-y-3">
                            {order.comments.map((c) => (
                                <div key={c.id} className="text-sm">
                                    <p className="font-medium">
                                        {c.user?.name}
                                        <span className="text-xs text-muted-foreground font-normal ml-2">
                                            {new Date(c.createdAt).toLocaleString()}
                                        </span>
                                    </p>
                                    <p className="text-muted-foreground whitespace-pre-wrap">{c.body}</p>
                                </div>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Audit trail */}
            <Card>
                <CardContent className="p-4 md:p-6">
                    <h2 className="font-semibold mb-3">Audit trail</h2>
                    <div className="space-y-2 text-sm">
                        {order.history.map((h) => {
                            const reason = h.reason;
                            // Evidence rows — line adds/edits/removals, photos, documents,
                            // comments, signatures — carry fromStatus === toStatus: the status
                            // did not change, the reason text says what actually happened
                            // (§12.3). Status moves and the creation row render as before.
                            const evidence = h.fromStatus !== null && h.fromStatus === h.toStatus;
                            const sep = evidence && reason ? reason.indexOf(' — ') : -1;
                            const kind = reason && sep > 0 ? reason.slice(0, sep) : null;
                            const detail = reason && sep > 0
                                ? reason.slice(sep + 3)
                                : (evidence && reason ? reason : null);
                            return (
                                <div key={h.id} className="flex flex-wrap items-baseline gap-x-2">
                                    <span className="text-muted-foreground text-xs tabular-nums">
                                        {new Date(h.createdAt).toLocaleString()}
                                    </span>
                                    {evidence ? (
                                        <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground bg-muted rounded px-1.5 py-0.5">
                                            {kind ?? 'note'}
                                        </span>
                                    ) : (
                                        <span>
                                            {h.fromStatus
                                                ? `${statusLabel(h.fromStatus)} → ${statusLabel(h.toStatus)}`
                                                : `Created as ${statusLabel(h.toStatus)}`}
                                        </span>
                                    )}
                                    <span className="text-muted-foreground">· {h.actorName}</span>
                                    {detail && <span className="text-muted-foreground w-full">{detail}</span>}
                                    {!evidence && h.reason && (
                                        <span className="text-muted-foreground w-full">{h.reason}</span>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </CardContent>
            </Card>

            {/* Void dialog */}
            <Dialog open={voidOpen} onOpenChange={setVoidOpen}>
                <DialogContent className="sm:max-w-[520px]">
                    <DialogHeader>
                        <DialogTitle>Void {order.orderNumber}</DialogTitle>
                        <DialogDescription>
                            The full record — lines, photos, signatures — is snapshotted into the audit
                            trail before anything changes. Voiding is permanent.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Label htmlFor="void-code">Reason category</Label>
                            <Select value={voidCode} onValueChange={setVoidCode}>
                                <SelectTrigger id="void-code"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {(meta?.voidReasonCodes ?? []).map((c) => (
                                        <SelectItem key={c} value={c}>
                                            <span className="capitalize">{c.replace(/_/g, ' ')}</span>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label htmlFor="void-reason">
                                Reason * (at least {meta?.minVoidReasonLength ?? 20} characters)
                            </Label>
                            <Textarea
                                id="void-reason"
                                rows={3}
                                value={voidReason}
                                onChange={(e) => setVoidReason(e.target.value)}
                            />
                            <p className="text-xs text-muted-foreground mt-1">
                                {voidReason.trim().length}/{meta?.minVoidReasonLength ?? 20}
                            </p>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setVoidOpen(false)}>Cancel</Button>
                        <Button
                            variant="destructive"
                            disabled={busy !== null || voidReason.trim().length < (meta?.minVoidReasonLength ?? 20)}
                            onClick={async () => {
                                await handleTransition('voided', { reason: voidReason.trim(), voidReasonCode: voidCode });
                                setVoidOpen(false);
                            }}
                        >
                            <Ban size={16} className="mr-2" />Void Order
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <GenerateOrderDialog
                open={aiOpen}
                onOpenChange={setAiOpen}
                products={products}
                onConfirm={(generated, raw, files, usageId) =>
                    handleGenerated(generated, raw, files, usageId)}
            />

            {/* Mark Entered dialog */}
            <Dialog open={enterOpen} onOpenChange={setEnterOpen}>
                <DialogContent className="sm:max-w-[480px]">
                    <DialogHeader>
                        <DialogTitle>Mark {order.orderNumber} Entered</DialogTitle>
                        <DialogDescription>
                            Confirms the counted numbers have been keyed into the external stock system.
                            This is terminal and the reference cannot be edited afterwards.
                        </DialogDescription>
                    </DialogHeader>
                    <div>
                        <Label htmlFor="external-ref">External Reference Number *</Label>
                        <Input
                            id="external-ref"
                            value={externalRef}
                            maxLength={16}
                            onChange={(e) => setExternalRef(e.target.value)}
                            placeholder="e.g. SR-104882"
                            className="font-mono"
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                            Max 16 characters. This is the join between Idarah and the external system
                            during a dispute.
                        </p>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEnterOpen(false)}>Cancel</Button>
                        <Button
                            disabled={busy !== null || !externalRef.trim()}
                            onClick={async () => {
                                await handleTransition('entered', { externalRef: externalRef.trim() });
                                setEnterOpen(false);
                            }}
                        >
                            <Send size={16} className="mr-2" />Mark Entered
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Batch upload — one row per picked photo: thumbnail left, slot picker right. */}
            <Dialog open={batchOpen} onOpenChange={(open) => { if (!open) closeBatch(); }}>
                <DialogContent className="sm:max-w-[640px] max-h-[85vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Assign photos to slots</DialogTitle>
                        <DialogDescription>
                            Pick which angle each photo shows. Every photo needs a slot before
                            you can save, and each slot can only be used once. To change a photo
                            that is already saved, use its edit button instead.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        {batchPhotos.map((b) => (
                            <div key={b.id} className="flex items-center gap-3">
                                <img
                                    src={b.previewUrl}
                                    alt={b.file.name}
                                    className="w-20 h-20 md:w-24 md:h-24 object-cover rounded-md border border-border shrink-0"
                                />
                                <div className="flex-1 min-w-0">
                                    <Select
                                        value={b.slotKey}
                                        onValueChange={(v) => setBatchSlot(b.id, v)}
                                    >
                                        <SelectTrigger>
                                            <SelectValue placeholder="Which angle is this?" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {emptySlots.map((s) => (
                                                <SelectItem
                                                    key={s.key}
                                                    value={s.key}
                                                    disabled={batchPhotos.some(
                                                        (r) => r.id !== b.id && r.slotKey === s.key)}
                                                >
                                                    {s.label}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    aria-label="Remove photo"
                                    disabled={busy === 'batchPhotos'}
                                    onClick={() => removeBatchRow(b.id)}
                                >
                                    <X size={16} />
                                </Button>
                            </div>
                        ))}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" disabled={busy === 'batchPhotos'} onClick={closeBatch}>
                            Cancel
                        </Button>
                        <Button
                            disabled={busy !== null || batchPhotos.length === 0
                                || batchPhotos.some((b) => !b.slotKey)}
                            onClick={handleBatchSave}
                        >
                            {busy === 'batchPhotos' && <Loader2 size={16} className="animate-spin mr-2" />}
                            Save {batchPhotos.length} photo(s)
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Remove a source document — a wrongly attached file can be corrected (§12.4) */}
            <Dialog open={docToDelete !== null} onOpenChange={(o) => { if (!o) setDocToDelete(null); }}>
                <DialogContent className="sm:max-w-[480px]">
                    <DialogHeader>
                        <DialogTitle>Remove source document?</DialogTitle>
                        <DialogDescription>
                            Removes {docToDelete?.filename} from {order.orderNumber}. The removal is
                            snapshotted into the audit trail, so who deleted it and when is never lost.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" disabled={busy !== null}
                                onClick={() => setDocToDelete(null)}>Cancel</Button>
                        <Button variant="destructive" disabled={busy !== null}
                                onClick={async () => {
                                    if (!docToDelete) return;
                                    const target = docToDelete;
                                    await run('delDoc',
                                        () => returnsApi.deleteDocument(order.id, target.id),
                                        'Document removed');
                                    setDocToDelete(null);
                                }}>
                            {busy === 'delDoc' && <Loader2 size={16} className="animate-spin mr-2" />}
                            Remove document
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <ImageLightbox
                alt="Truck photo"
                images={capturedSlots.map((s) => {
                    const p = photosByStage.get(s.key)!;
                    return {
                        id: p.id,
                        url: p.url,
                        label: s.label,
                        // serverTimestamp, not deviceTimestamp — §12.1 records the device
                        // clock but does not trust it.
                        caption: [p.capturedBy?.name, new Date(p.serverTimestamp).toLocaleString()]
                            .filter(Boolean).join(' · '),
                    };
                })}
                index={photoIndex}
                onIndexChange={setPhotoIndex}
            />
        </div>
    );
}
